import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useGlobals } from "../hooks/useGlobals";
import { Messages } from "../lib/config";
import MemberService from "../services/MemberService";
import { useTranslation } from "react-i18next";
import { MemberType } from "../lib/enums/member.enum";

function Navbar(props: any) {
  const { visible } = props;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const { authMember, setAuthMember } = useGlobals();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("lang", lng);
    setIsLangOpen(false);
  };

  const handleLogout = async () => {
    try {
      const memberService = new MemberService();
      await memberService.logout();
      setAuthMember(null);
      setIsProfileMenuOpen(false);
      setIsMenuOpen(false);
      if (location.pathname === "/my-page") {
        navigate("/");
      }
    } catch (err) {
      console.log(err);
      alert(Messages.error1);
    }
  };

  const DefaultAvatar = (name: string) => {
    const initials = name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center border-2 border-cyan-500">
        <span className="text-white text-sm font-semibold">{initials}</span>
      </div>
    );
  };

  const getLangFlag = () => {
    const current = i18n.resolvedLanguage;
    if (current === "en") return "🇺🇸";
    if (current === "uz") return "🇺🇿";
    if (current === "ko") return "🇰🇷";
    return "🇺🇸";
  };

  return (
    <nav
      className={
        !visible
          ? "bg-white shadow-lg sticky top-0 z-50"
          : "bg-white shadow-lg sticky top-[35px] z-50"
      }
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center group"
            style={{ height: "80px", overflow: "hidden" }}
          >
            <img
              src="/logo.png"
              alt="Intervify Logo"
              style={{ height: "300px", width: "auto" }}
              className="object-contain transition-opacity duration-300"
            />
          </Link>

          <div className="flex">
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1 mr-6">
              <Link
                to="/"
                className="text-gray-700 hover:text-cyan-600 font-medium transition-colors px-4 py-2 rounded-lg hover:bg-cyan-50"
              >
                {t("navbar.home")}
              </Link>
              <Link
                to="/practice"
                className="text-gray-700 hover:text-cyan-600 font-medium transition-colors px-4 py-2 rounded-lg hover:bg-cyan-50"
              >
                {t("navbar.practice")}
              </Link>

              {authMember?.memberType === MemberType.ADMIN && (
                <Link
                  to="/admin"
                  className="text-gray-700 hover:text-cyan-600 font-medium transition-colors px-4 py-2 rounded-lg hover:bg-cyan-50"
                >
                  Admin
                </Link>
              )}
            </div>

            {/* Right Section */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Profile Section */}
              {authMember ? (
                <div className="flex items-center space-x-4">
                  {/* Profile Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                      className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {authMember.memberImage ? (
                        <img
                          src={authMember.memberImage || "/default-avatar.png"}
                          alt={authMember.memberName}
                          className="w-10 h-10 rounded-full object-cover border-2 border-cyan-500"
                        />
                      ) : (
                        DefaultAvatar(authMember.memberName)
                      )}
                      <div className="text-left">
                        <p className="text-sm font-semibold text-gray-900">
                          {authMember.memberName}
                        </p>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-600 transition-transform ${
                          isProfileMenuOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {isProfileMenuOpen && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl py-2 ring-1 ring-black ring-opacity-10">
                        <Link
                          to="/my-page"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="block px-4 py-3 text-gray-700 hover:bg-cyan-50 hover:text-cyan-600 transition-colors"
                        >
                          {t("navbar.myPage")}
                        </Link>
                        <hr className="my-2" />
                        <button
                          onClick={handleLogout}
                          className="block w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 transition-colors"
                        >
                          {t("navbar.logout")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
                >
                  {t("navbar.login")}
                </Link>
              )}

              {/* Language Selector Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <span className="text-2xl">{getLangFlag()}</span>
                  <svg
                    className={`w-4 h-4 text-gray-700 transition-transform ${
                      isLangOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {isLangOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl py-2 ring-1 ring-black ring-opacity-10">
                    <button
                      onClick={() => changeLanguage("en")}
                      className={`w-full text-left px-4 py-3 flex items-center space-x-3 transition-colors ${
                        i18n.resolvedLanguage === "en"
                          ? "bg-cyan-50 text-cyan-600"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <span className="text-lg font-semibold">🇺🇸</span>
                      <div>
                        <p className="font-semibold">English</p>
                        <p className="text-xs text-gray-500">EN</p>
                      </div>
                      {i18n.resolvedLanguage === "en" && (
                        <svg
                          className="w-5 h-5 ml-auto text-cyan-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => changeLanguage("uz")}
                      className={`w-full text-left px-4 py-3 flex items-center space-x-3 transition-colors ${
                        i18n.resolvedLanguage === "uz"
                          ? "bg-cyan-50 text-cyan-600"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <span className="text-lg font-semibold">🇺🇿</span>
                      <div>
                        <p className="font-semibold">O'zbekcha</p>
                        <p className="text-xs text-gray-500">UZ</p>
                      </div>
                      {i18n.resolvedLanguage === "uz" && (
                        <svg
                          className="w-5 h-5 ml-auto text-cyan-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => changeLanguage("ko")}
                      className={`w-full text-left px-4 py-3 flex items-center space-x-3 transition-colors ${
                        i18n.resolvedLanguage === "ko"
                          ? "bg-cyan-50 text-cyan-600"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <span className="text-lg font-semibold">🇰🇷</span>
                      <div>
                        <p className="font-semibold">한국어</p>
                        <p className="text-xs text-gray-500">KO</p>
                      </div>
                      {i18n.resolvedLanguage === "ko" && (
                        <svg
                          className="w-5 h-5 ml-auto text-cyan-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden inline-flex items-center justify-center p-2 rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-gray-50">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/"
              className="block px-3 py-3 rounded-lg text-gray-700 hover:bg-cyan-100 hover:text-cyan-600 font-medium transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("navbar.home")}
            </Link>
            <Link
              to="/practice"
              className="block px-3 py-3 rounded-lg text-gray-700 hover:bg-cyan-100 hover:text-cyan-600 font-medium transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("navbar.practice")}
            </Link>

            <hr className="my-3" />

            {/* Language Selector Mobile */}
            <div className="px-3 py-2">
              <p className="text-sm font-semibold text-gray-600 mb-3">
                {t("navbar.language")}
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    changeLanguage("en");
                    setIsMenuOpen(false);
                  }}
                  className={`w-full px-4 py-3 rounded-lg text-left flex items-center space-x-3 transition-colors ${
                    i18n.resolvedLanguage === "en"
                      ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                      : "bg-white text-gray-700 border border-gray-300 hover:border-cyan-600"
                  }`}
                >
                  <span className="text-lg">🇺🇸</span>
                  <div>
                    <p className="font-semibold">English</p>
                    <p className="text-xs opacity-75">EN</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    changeLanguage("uz");
                    setIsMenuOpen(false);
                  }}
                  className={`w-full px-4 py-3 rounded-lg text-left flex items-center space-x-3 transition-colors ${
                    i18n.resolvedLanguage === "uz"
                      ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                      : "bg-white text-gray-700 border border-gray-300 hover:border-cyan-600"
                  }`}
                >
                  <span className="text-lg">🇺🇿</span>
                  <div>
                    <p className="font-semibold">O'zbekcha</p>
                    <p className="text-xs opacity-75">UZ</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    changeLanguage("ko");
                    setIsMenuOpen(false);
                  }}
                  className={`w-full px-4 py-3 rounded-lg text-left flex items-center space-x-3 transition-colors ${
                    i18n.resolvedLanguage === "ko"
                      ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                      : "bg-white text-gray-700 border border-gray-300 hover:border-cyan-600"
                  }`}
                >
                  <span className="text-lg">🇰🇷</span>
                  <div>
                    <p className="font-semibold">한국어</p>
                    <p className="text-xs opacity-75">KO</p>
                  </div>
                </button>
              </div>
            </div>

            <hr className="my-3" />

            {/* Profile Mobile */}
            {authMember ? (
              <div className="px-3 py-2">
                <div className="flex items-center space-x-3 mb-3">
                  {authMember.memberImage ? (
                    <img
                      src={authMember.memberImage || "/default-avatar.png"}
                      alt={authMember.memberName}
                      className="w-10 h-10 rounded-full object-cover border-2 border-cyan-500"
                    />
                  ) : (
                    DefaultAvatar(authMember.memberName)
                  )}
                  <span className="text-gray-900 font-semibold">
                    {authMember.memberName}
                  </span>
                </div>
                <Link
                  to="/my-page"
                  className="block px-3 py-3 rounded-lg text-gray-700 hover:bg-cyan-100 hover:text-cyan-600 font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("navbar.myPage")}
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-3 rounded-lg text-red-600 hover:bg-red-50 font-medium transition-colors"
                >
                  {t("navbar.logout")}
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="block px-3 py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold hover:from-cyan-600 hover:to-blue-700 transition-all text-center shadow-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("navbar.login")}
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
